import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import {
  createOrganizationAndInviteAdmin,
  getAllOrganizationsStats,
  sendInvitationEmail,
  deleteOrganization
} from '../utils/superAdmin';
import { 
  Crown, 
  Building, 
  Users, 
  Mail, 
  Plus, 
  Search, 
  Calendar, 
  BarChart2, 
  AlertCircle, 
  Check, 
  X,
  Trash2,
  Info,
  Trophy
} from 'lucide-react';
import type { OrganizationStats } from '../types';
import LiquidGlass from './LiquidGlass';

export default function SuperAdminPanel() {
  const { user } = useAuth();
  const { userProfile } = useOrganization();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationStats[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationStats | null>(null);
  const [deletingOrganization, setDeletingOrganization] = useState<OrganizationStats | null>(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    organizationName: '',
    adminEmail: '',
    adminFirstName: '' as string,
    adminLastName: '' as string,
    totalSeats: 5
  });

  useEffect(() => {
    // Check if user is SuperAdmin
    if (userProfile && userProfile.role !== 'SuperAdmin') {
      navigate('/dashboard');
    } else {
      loadOrganizations();
    }
  }, [userProfile, navigate]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await getAllOrganizationsStats();
      setOrganizations(data);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create organization and get invitation details
      const result = await createOrganizationAndInviteAdmin({
        organizationName: formData.organizationName,
        adminEmail: formData.adminEmail,
        adminFirstName: formData.adminFirstName.trim() || null,
        adminLastName: formData.adminLastName.trim() || null,
        totalSeats: formData.totalSeats
      });

      console.log("Organization created successfully:", result);

      // Handle the case where result is an array (from Supabase RPC)
      const resultData = Array.isArray(result) ? result[0] : result;

      if (!resultData || !resultData.invitation_id || !resultData.invitation_token) {
        throw new Error('Failed to create organization: Invalid response from server');
      }

      // Send invitation email
      await sendInvitationEmail({
        invitationId: resultData.invitation_id,
        token: resultData.invitation_token,
        organizationName: formData.organizationName,
        recipientEmail: formData.adminEmail,
        adminName: userProfile?.first_name 
          ? `${userProfile.first_name} ${userProfile.last_name || ''}`
          : undefined
      });

      setSuccess(`Organization "${formData.organizationName}" created and invitation sent to ${formData.adminEmail}`);
      setFormData({
        organizationName: '',
        adminEmail: '',
        adminFirstName: '',
        adminLastName: '',
        totalSeats: 5
      });
      setShowCreateForm(false);
      await loadOrganizations();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrganization = (organization: OrganizationStats) => {
    setSelectedOrganization(organization);
  };

  const handleDeleteOrganization = async (organization: OrganizationStats) => {
    if (!confirm(`Are you sure you want to delete the organization "${organization.name}"? This action cannot be undone and will delete all associated data.`)) {
      return;
    }

    setDeletingInProgress(true);
    setError(null);

    try {
      await deleteOrganization(organization.id);
      setSuccess(`Organization "${organization.name}" has been deleted successfully.`);
      setDeletingOrganization(null);
      await loadOrganizations();
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      setError(err.message);
    } finally {
      setDeletingInProgress(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredOrganizations = organizations.filter(org => 
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!userProfile || userProfile.role !== 'SuperAdmin') {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <div className="bg-red-50 rounded-lg p-8 shadow-sm">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. This area is restricted to Super Administrators only.
          </p>
          <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 text-white font-medium"
            >
              Return to Dashboard
            </button>
          </LiquidGlass>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown className="w-8 h-8 text-amber-500" />
          <h1 className="text-3xl font-bold text-gray-800">Super Admin Panel</h1>
        </div>
        <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-6 py-3 text-white font-medium"
          >
            <Plus className="w-4 h-4" />
            New Organization
          </button>
        </LiquidGlass>
      </div>

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </LiquidGlass>
      )}

      {success && (
        <LiquidGlass className="bg-green-100/60 border-green-200/60 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        </LiquidGlass>
      )}

      {/* Create Organization Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-2xl p-6" bgOpacity="ultra">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Building className="w-6 h-6 text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-800">Create New Organization</h2>
              </div>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOrganization} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Seats
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.totalSeats}
                  onChange={(e) => setFormData({ ...formData, totalSeats: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Maximum number of users allowed in this organization
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">First Admin User</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    An invitation will be sent to this email address
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.adminFirstName}
                      onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
                      className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.adminLastName}
                      onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
                      className="w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 text-white font-medium disabled:opacity-50"
                  >
                    <Building className="w-4 h-4" />
                    {loading ? 'Creating...' : 'Create Organization'}
                  </button>
                </LiquidGlass>
              </div>
            </form>
          </LiquidGlass>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg"
        />
      </div>

      {/* Organizations List */}
      <LiquidGlass variant="card" className="overflow-hidden relative">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Events
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/60 divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredOrganizations.length > 0 ? (
                filteredOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-white/80 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Building className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{org.name}</div>
                          <div className="text-sm text-gray-500">ID: {org.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">{formatDate(org.created_at)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">
                          {org.active_users_count} / {org.total_seats}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Trophy className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">{org.total_events}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BarChart2 className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">{org.total_participants}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewOrganization(org)}
                          className="text-blue-600 hover:text-blue-800 transition-colors p-1.5 rounded-md hover:bg-blue-50"
                          title="View organization details"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingOrganization(org)}
                          className="text-red-600 hover:text-red-800 transition-colors p-1.5 rounded-md hover:bg-red-50"
                          title="Delete organization"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm ? 'No organizations found matching your search.' : 'No organizations found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </LiquidGlass>

      {/* Organization Details Modal */}
      {selectedOrganization && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-3xl p-6" bgOpacity="ultra">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Building className="w-6 h-6 text-gray-700" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">{selectedOrganization.name}</h2>
                  <p className="text-sm text-gray-500">Organization Details</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrganization(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Created</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="font-medium">{formatDate(selectedOrganization.created_at)}</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Users</p>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <p className="font-medium">{selectedOrganization.active_users_count} / {selectedOrganization.total_seats}</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">ID</p>
                  <p className="font-mono text-xs text-gray-600 truncate">{selectedOrganization.id}</p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium mb-4">Organization Users</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-center text-gray-500 py-2">
                    {selectedOrganization.active_users_count > 0 ? 
                      `This organization has ${selectedOrganization.active_users_count} active users.` : 
                      'This organization has no active users.'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium mb-4">Activity Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Total Events</p>
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-gray-400" />
                      <p className="font-medium">{selectedOrganization.total_events}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Total Participants</p>
                    <div className="flex items-center gap-2">
                      <BarChart2 className="w-4 h-4 text-gray-400" />
                      <p className="font-medium">{selectedOrganization.total_participants}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setSelectedOrganization(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setDeletingOrganization(selectedOrganization);
                    setSelectedOrganization(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 border border-red-200 rounded-md hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Organization
                </button>
              </div>
            </div>
          </LiquidGlass>
        </div>
      )}

      {/* Delete Organization Confirmation Modal */}
      {deletingOrganization && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-md p-6" bgOpacity="ultra">
            <div className="flex items-center gap-3 mb-6">
              <Trash2 className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-800">Delete Organization</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-medium">{deletingOrganization.name}</span>? This action cannot be undone and will delete all associated data including users, events, and results.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingOrganization(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={deletingInProgress}
              >
                Cancel
              </button>
              <LiquidGlass variant="button" hover glowColor="white">
                <button
                  onClick={() => handleDeleteOrganization(deletingOrganization)}
                  disabled={deletingInProgress}
                  className="px-4 py-2 text-red-600 hover:text-red-700 flex items-center gap-2"
                >
                  {deletingInProgress ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Organization
                    </>
                  )}
                </button>
              </LiquidGlass>
            </div>
          </LiquidGlass>
        </div>
      )}

      {/* Info Card */}
      <LiquidGlass className="p-4 bg-blue-50/60 border-blue-100/60">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800 mb-1">Super Admin Instructions</h3>
            <p className="text-sm text-blue-700">
              As a Super Admin, you can create new organizations and invite their first admin user.
              The admin will receive an email invitation to set up their account and can then invite
              additional users to their organization.
            </p>
          </div>
        </div>
      </LiquidGlass>
    </div>
  );
}